import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../../core/reducers';
import { Template, TemplateKeyProps } from '../../models';
import { CreateTemplateOnServer, UpdateTemplateOnServer } from '../../actions';

@Component({
  selector: 'kt-create-template',
  templateUrl: './create-template.component.html',
  styleUrls: ['./create-template.component.scss'],
})
export class CreateTemplateComponent implements OnInit {
  form: FormGroup;
  keys: TemplateKeyProps[];
  keyIndex = '';
  hasKeys = true;
  @ViewChild('key') key: ElementRef;
  @ViewChild('value') value: ElementRef;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { template: Template; viewMode: boolean },
    private dialogRef: MatDialogRef<CreateTemplateComponent>,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      name: [this.data.template?.name || '', [Validators.required, Validators.pattern(/\.odt$/i)]],
      file: [
        this.data.template?.template_file_base64,
        this.data.template?.id ? [] : Validators.required,
      ],
    });

    this.keys = this.data.template?.keys ? JSON.parse(this.data.template.keys) : [];

    if (this.data.viewMode)
      Object.keys(this.form.controls).forEach((key) => this.form.get(key).disable());
  }

  clickFileInput(event: Event, fileInput: HTMLInputElement): void {
    event.stopPropagation();

    if (!this.data.viewMode) {
      fileInput.click();
      this.form.get('file').markAsTouched();
    }
  }

  changeFile(event: Event): void {
    const target = event.target as HTMLInputElement;

    if (target?.files?.length) {
      const file = target.files[0];
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        this.form.get('name').patchValue(file.name);
        this.form.get('file').patchValue((reader.result as string)?.split(',')[1]);
      };
    }
  }

  downloadFile(event: Event): void {
    event.stopPropagation();

    window.open(this.data.template?.url, '_blank').focus();
  }

  addEditKey(event: Event): void {
    event.stopPropagation();

    const templateKey: TemplateKeyProps = {
      key: this.key.nativeElement.value,
      value: this.value.nativeElement.value,
    };

    this.keys.push(templateKey);

    this.keyIndex = '';
    this.key.nativeElement.value = '';
    this.value.nativeElement.value = '';
  }

  prepareEditKey(event: Event, index: number): void {
    event.stopPropagation();

    this.keyIndex = index.toString();

    const templateKey = this.keys[index];

    this.keys.splice(index, 1);
    this.key.nativeElement.value = templateKey.key;
    this.value.nativeElement.value = templateKey.value;
  }

  deleteKey(event: Event, index: number): void {
    event.stopPropagation();

    this.keys.splice(index, 1);
  }

  getTitle(): string {
    return `FORMS_TEMPLATES_MODULE.TEMPLATE.${
      this.data.template?.id ? (this.data.viewMode ? 'VIEW_TITLE' : 'UPDATE_TITLE') : 'CREATE_TITLE'
    }`;
  }

  isInvalid(name: string, error: string): boolean {
    const field = this.form.get(name);

    return field.touched && field.hasError(error);
  }

  getErrorMessage(label: string, error: string): string {
    switch (error) {
      case 'required':
        return `${label} field is required`;
      case 'pattern':
        return `${label} field is invalid! Accepted type .odt`;
    }
  }

  closeDialog(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }

  submit(event: Event): void {
    event.stopPropagation();

    this.form.markAllAsTouched();

    if (this.form.invalid) return;

    if (!this.keys.length) {
      this.hasKeys = false;
      return;
    }

    this.hasKeys = true;

    const template = new Template(
      this.data.template?.id,
      this.form.get('name').value,
      JSON.stringify(this.keys),
      this.form.get('file').value,
    );
    if (!this.data.template?.id) delete template.id;

    for (const [key, value] of Object.entries(template)) {
      if (typeof value === 'string') template[key] = value.trim();
    }

    this.data.template?.id
      ? this.store.dispatch(new UpdateTemplateOnServer({ template }))
      : this.store.dispatch(new CreateTemplateOnServer({ template }));

    this.dialogRef.close({ template });
  }
}
